import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { ZodObject, ZodRawShape } from 'zod';

interface UseGetQueryParams<T> {
  queryKey: any[];
  fetchFunction: (params?: any) => Promise<T | null>;
  isEntity: (obj: any) => obj is T;
  functionName?: string;
  enabled: boolean;
  params: any;
  displayNotification?: boolean;
  validationSchema?: ZodObject<ZodRawShape>;
}

/**
 * Custom hook for fetching data using a query key and handling error notifications.
 *
 * @param queryKey - The key for the query.
 * @param fetchFunction - The function to fetch data for the query.
 * @param isEntity - Function to check if an object is of type T.
 * @param functionName - The name of the function.
 * @param params - Parameters for the fetch function.
 * @param enabled - Flag to enable/disable the query.
 * @param displayNotification - Flag to display error notifications (default is true).
 *
 * @returns The result of the query with data of type T or null, and potential error of type Error.
 */

export const useGetQuery = <T>({
  queryKey,
  fetchFunction,
  isEntity,
  params,
  enabled,
  validationSchema,
}: UseGetQueryParams<T>): UseQueryResult<T | null, Error> => {
  const validationErrors: any[] = [];
  return useQuery<T | null, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKey,
    queryFn: async () => {
      // Fetch data from the API;
      const data = await fetchFunction(params);
      if (!isEntity(data)) {
        if (validationSchema) {
          validationErrors.push(validationSchema.safeParse(data).error);
        }
      }
      return data;
    },
    enabled: enabled,
    retry: false,
  });
};
